import React, { useEffect, useState, useRef } from "react";
import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import { Table } from "antd";
import * as XLSX from "xlsx";
import {
  useCreateStockLogMutation,
  useProductTypeQuery,
  useServiceProductsQuery,
  useServicesQuery,
  useUpdateProductOrServieMutation,
} from "../../../Store/Apis/servicesApi";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  isDropdownPermission,
  isPermissionGranted,
} from "../../../Hooks/permissionsMiddleware";
import { useOutletsQuery } from "../../../Store/Apis/outletApi";
import Loader from "../../../Components/Loader/Loader";

const RateChart = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [sortFilter, setSortFilter] = useState({
    search: "",
    filter: "",
    type: "",
    outletID: "",
  });

  const outletList = useOutletsQuery();
  const serviceList = useServicesQuery();
  const serviceTypeApi = useProductTypeQuery({
    outletID: sortFilter.outletID,
  });

  const [updateProductOrService, updateProductOrServiceResponse] =
    useUpdateProductOrServieMutation();
  const [createStockLog] = useCreateStockLogMutation();
  const serviceProductsApi = useServiceProductsQuery({
    keyname: sortFilter.search,
    keyID: sortFilter.type,
    keyCategory: sortFilter.filter,
    outletID: sortFilter.outletID,
  });

  // Transform the data
  const transformedData = serviceProductsApi?.data?.data
    ?.flatMap((parent) =>
      parent.childRecords.map((child) => ({
        parentId: parent.parentId,
        productServiceName: parent.productServiceName,
        brandID: parent.brandID,
        brandName: parent.brandName,
        outletName: parent.outletName,
        serviceTypeName: parent.serviceTypeName,
        serviceCategoryName: parent.serviceCategoryName,
        isActive: parent.isActive,
        type: parent.type,
        dateCreated: parent.dateCreated,
        childId: child.childId,
        typeId: child.typeId,
        code: child.code,
        reorderLevel: child.reorderLevel,
        rate: child.rate,
        costPrice: child.costPrice,
        quantity: child.quantity,
        icon: child.icon,
        description: child.description,
        typeName: child.typeName,
      }))
    )
    ?.filter((item) => {
      // If user role_id is 7, only show items with type: 1 (products)
      if (user.role_Id === 7) {
        return item.type === 1;
      }
      // For all other roles, show all items
      return true;
    });

  const [rateData, setRateData] = useState({}); // Manage rate values at component level
  const [showModal, setShowModal] = useState(false); // Modal state
  const [selectedItem, setSelectedItem] = useState(null); // Selected item for adding stock
  const [newStockQuantity, setNewStockQuantity] = useState(""); // Input for new stock
  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // Confirmation modal state
  const [confirmationAction, setConfirmationAction] = useState(null); // Action to confirm

  // Bulk Stock Update modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef();
  // Export Rate Chart to Excel
  const handleExportRateChart = () => {
    if (!transformedData || transformedData.length === 0) {
      toast.error("No data to export");
      return;
    }
    // Prepare data for Excel
    const exportData = transformedData.map((item) => ({
      "Product/Service Name": item.productServiceName,
      Code: item.code,
      Brand: item.brandName,
      Category: item.serviceCategoryName,
      Type: item.type === 1 ? "Product" : "Service",
      "Service Type": item.typeName,
      Quantity: item.quantity,
      Rate: item.rate,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RateChart");
    XLSX.writeFile(workbook, "RateChart.xlsx");
  };

  // Handle file selection and preview
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // File validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("File size should be less than 5MB");
      e.target.value = "";
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const importedData = XLSX.utils.sheet_to_json(worksheet);

        // Validate required columns
        if (importedData.length === 0) {
          toast.error("Excel file is empty");
          setSelectedFile(null);
          e.target.value = "";
          return;
        }

        const requiredColumns = ["Code", "Rate", "Quantity"];
        const firstRow = importedData[0];
        const missingColumns = requiredColumns.filter(
          (col) => !(col in firstRow)
        );

        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(", ")}`);
          setSelectedFile(null);
          e.target.value = "";
          return;
        }

        // Create preview data showing matches
        const preview = importedData.map((row) => {
          const match = transformedData.find(
            (item) => item.code === row["Code"]
          );
          return {
            code: row["Code"] || "",
            currentRate: match ? match.rate : "N/A",
            newRate: row["Rate"] || 0,
            currentQuantity: match ? match.quantity : "N/A",
            newQuantity: row["Quantity"] || 0,
            productName: match ? match.productServiceName : "Not Found",
            found: !!match,
          };
        });

        setPreviewData(preview);
        toast.success(
          `File loaded successfully! Found ${
            preview.filter((p) => p.found).length
          } matching items.`
        );
      } catch (error) {
        console.error("Error reading file:", error);
        toast.error("Error reading Excel file. Please check the file format.");
        setSelectedFile(null);
        e.target.value = "";
      }
    };
    reader.onerror = () => {
      toast.error("Error reading file");
      setSelectedFile(null);
      e.target.value = "";
    };
    reader.readAsArrayBuffer(file);
  };

  // Process the upload after confirmation
  const processUpload = async () => {
    if (!selectedFile || previewData.length === 0) return;

    setBulkImportLoading(true);
    let updateCount = 0;
    let updatePromises = [];

    // Group updates by parentId to avoid multiple updates to same parent
    const parentUpdates = {};

    for (const row of previewData) {
      if (!row.found) continue;

      const match = transformedData.find((item) => item.code === row.code);
      if (match) {
        const parentId = match.parentId;

        if (!parentUpdates[parentId]) {
          const parentItem = serviceProductsApi?.data?.data.find(
            (elem) => elem.parentId === parentId
          );
          if (parentItem) {
            parentUpdates[parentId] = {
              parentItem: parentItem,
              childRecords: [...parentItem.childRecords],
            };
          }
        }

        // Update the specific child record in the grouped update
        if (parentUpdates[parentId]) {
          parentUpdates[parentId].childRecords = parentUpdates[
            parentId
          ].childRecords.map((child) => {
            if (child.code === row.code) {
              return {
                ...child,
                quantity: row.newQuantity,
                rate: row.newRate,
              };
            }
            return child;
          });
        }
      }
    }

    // Process all parent updates
    for (const parentId in parentUpdates) {
      const { parentItem, childRecords } = parentUpdates[parentId];

      const updatedParentItem = {
        id: parentItem.parentId,
        productServiceName: parentItem.productServiceName,
        brandId: parentItem.brandID,
        isActive: parentItem.isActive ? 1 : 0,
        type: parentItem.type,
        outletId: user.outletID,
        iconID: parentItem.iconID,
        childRecords: childRecords,
      };

      updatePromises.push(
        updateProductOrService(updatedParentItem)
          .unwrap()
          .then(() => {
            // Count how many items were actually updated in this parent
            const updatedItems = childRecords.filter((child) =>
              previewData.some(
                (preview) => preview.code === child.code && preview.found
              )
            );
            updateCount += updatedItems.length;
          })
          .catch((err) => {
            console.error("Bulk update error:", err);
          })
      );
    }

    await Promise.all(updatePromises);
    setBulkImportLoading(false);
    toast.success(`Done importing! Updated ${updateCount} items.`);
    setRateData({});
    setPreviewData([]);
    setSelectedFile(null);
    setShowBulkModal(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  // Bulk Stock Update button and modal
  const BulkStockUpdateModal = () => (
    <Modal
      show={showBulkModal}
      onHide={() => {
        setShowBulkModal(false);
        setPreviewData([]);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Bulk Stock Update</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label>Upload Excel File</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="form-control"
            disabled={bulkImportLoading}
          />
          <small className="text-muted">
            Select an Excel file to preview changes before uploading
          </small>
        </div>

        <div className="mb-3">
          <a
            href="#"
            onClick={handleExportRateChart}
            style={{ color: "blue", textDecoration: "underline" }}
          >
            Download Current Rate Chart Template
          </a>
        </div>

        {previewData.length > 0 && (
          <div className="mb-3">
            <h6>
              Preview Changes ({previewData.filter((item) => item.found).length}{" "}
              items will be updated)
            </h6>
            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Product</th>
                    <th>Current Rate</th>
                    <th>New Rate</th>
                    <th>Current Qty</th>
                    <th>New Qty</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((item, index) => (
                    <tr
                      key={index}
                      className={!item.found ? "table-warning" : ""}
                    >
                      <td>{item.code}</td>
                      <td>{item.productName}</td>
                      <td>{item.currentRate}</td>
                      <td
                        className={
                          item.currentRate !== item.newRate
                            ? "text-primary fw-bold"
                            : ""
                        }
                      >
                        {item.newRate}
                      </td>
                      <td>{item.currentQuantity}</td>
                      <td
                        className={
                          item.currentQuantity !== item.newQuantity
                            ? "text-primary fw-bold"
                            : ""
                        }
                      >
                        {item.newQuantity}
                      </td>
                      <td>
                        {item.found ? (
                          <span className="badge bg-success">Found</span>
                        ) : (
                          <span className="badge bg-warning">Not Found</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewData.some((item) => !item.found) && (
              <div className="alert alert-warning mt-2">
                <small>
                  Items marked as "Not Found" will be skipped during upload.
                </small>
              </div>
            )}
          </div>
        )}

        {bulkImportLoading && (
          <div className="mb-3 text-center">
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Uploading changes, please wait...
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setShowBulkModal(false);
            setPreviewData([]);
            setSelectedFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          disabled={bulkImportLoading}
        >
          Cancel
        </button>
        {!selectedFile && (
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => fileInputRef.current.click()}
            disabled={bulkImportLoading}
          >
            Select File
          </button>
        )}
        {previewData.length > 0 && (
          <button
            type="button"
            className="btn btn-success"
            onClick={processUpload}
            disabled={
              bulkImportLoading ||
              previewData.filter((item) => item.found).length === 0
            }
          >
            <i className="fas fa-upload me-1"></i>
            Start Upload ({previewData.filter((item) => item.found).length}{" "}
            items)
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );

  const handleRateChange = (item, value) => {
    setRateData((prev) => ({
      ...prev,
      [item.childId]: value, // Update the rateData state for the specific item
    }));
  };

  // Open modal and set the selected item
  const handleShowModal = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setNewStockQuantity(""); // Reset stock quantity input
  };

  // Handle adding new stock
  const handleAddStock = () => {
    setShowModal(false);
    setShowConfirmationModal(true);
    setConfirmationAction("addStock");
  };

  // Handle rate update (API call)
  const handleRateUpdate = (item) => {
    setSelectedItem(item);
    setShowConfirmationModal(true);
    setConfirmationAction("updateRate");
  };

  // Confirm action
  const confirmAction = () => {
    if (confirmationAction === "addStock") {
      // Find the parent item based on parentId.
      const filteredItem = serviceProductsApi?.data?.data.find(
        (elem) => elem.parentId === selectedItem.parentId
      );

      if (!filteredItem) {
        console.error("Parent item not found");
        return;
      }

      // Map over the childRecords to update the rate for the matching childId.
      const updatedChildRecords = filteredItem.childRecords.map((child) => {
        if (child.childId === selectedItem.childId) {
          return {
            ...child,
            quantity: Number(child.quantity) + Number(newStockQuantity), // Update the rate with the new rate
          };
        } else {
          return { ...child }; // Keep other child items unchanged
        }
      });

      const updatedParentItem = {
        id: filteredItem.parentId,
        productServiceName: filteredItem.productServiceName,
        brandId: filteredItem.brandID,
        isActive: filteredItem.isActive ? 1 : 0,
        type: filteredItem.type,
        childRecords: updatedChildRecords,
      };

      createStockLog({
        userId: user.id,
        serviceId: selectedItem.parentId,
        serviceTypeId: selectedItem.typeId,
        stockIn: newStockQuantity,
        stockOut: 0,
        remark: "",
      })
        .then((res) => {
          if (res?.data?.status == 200) {
            setShowConfirmationModal(false);
            setSelectedItem(null);
            setNewStockQuantity("");
            toast.success("New Stock Added !!", { duration: 7000 });
            navigate("/service/rate-chart");
          } else {
            toast.error(res?.data?.message, { duration: 7000 });
          }
        })
        .catch((err) => {
          toast.error("Something Went Wrong !!", { duration: 7000 });
        });
    } else if (confirmationAction === "updateRate") {
      const newRate = rateData[selectedItem.childId];

      // Find the parent item based on parentId.
      const filteredItem = serviceProductsApi?.data?.data.find(
        (elem) => elem.parentId === selectedItem.parentId
      );

      if (!filteredItem) {
        console.error("Parent item not found");
        return;
      }

      // Map over the childRecords to update the rate for the matching childId.
      const updatedChildRecords = filteredItem.childRecords.map((child) => {
        if (child.childId === selectedItem.childId) {
          return {
            ...child,
            rate: newRate, // Update the rate with the new rate
          };
        } else {
          return { ...child }; // Keep other child items unchanged
        }
      });

      // Create the updated parent object including the modified child records.
      const updatedParentItem = {
        id: filteredItem.parentId,
        productServiceName: filteredItem.productServiceName,
        brandId: filteredItem.brandID,
        isActive: filteredItem.isActive ? 1 : 0,
        type: filteredItem.type,
        outletId: user.outletID,
        iconID: filteredItem.iconID,
        childRecords: updatedChildRecords,
      };

      // Log the updated object (or you could dispatch it, save it, etc.)
      updateProductOrService(updatedParentItem)
        .then((res) => {
          console.log(res);
          if (res?.data?.status == 200) {
            setShowConfirmationModal(false);
            toast.success("Rate Updated !!", { duration: 7000 });
            navigate("/service/rate-chart");
          } else {
            toast.error(res?.data?.message, { duration: 7000 });
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something Went Wrong !!", { duration: 7000 });
        });
    }
  };

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outletID: user.outletID,
      });
    }
  }, []);

  const columns = [
    {
      title: "Product/Service Name",
      dataIndex: "productServiceName",
      key: "productServiceName",
      sorter: (a, b) => a.productServiceName.localeCompare(b.serviceName),
      filter: (value, record) =>
        record.productServiceName.toLowerCase().includes(value.toLowerCase()),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) =>
        record.productServiceName.toLowerCase().includes(value.toLowerCase()),
      render: (i, action) => (
        <>
          {console.log(action)}
          <p className="mb-1 text-sm fw-bold">
            {action.productServiceName} [{action.code}]
          </p>
          {action.type == 1 ? (
            <span className="badge bg-info text-white">Product</span>
          ) : (
            <span className="badge bg-warning text-dark">Service</span>
          )}
          <br />
          {
            <span className="badge bg-primary text-white mt-1">
              {action.outletName}
            </span>
          }
        </>
      ),
    },
    {
      title: "Brand",
      dataIndex: "brandName",
      key: "brandName",
      sorter: (a, b) => a.brandName.localeCompare(b.brandName),
      filter: (value, record) =>
        record.brandName.toLowerCase().includes(value.toLowerCase()),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) =>
        record.brandName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Category",
      dataIndex: "serviceCategoryName",
      key: "serviceCategoryName",
      sorter: (a, b) =>
        a.serviceCategoryName.localeCompare(b.serviceCategoryName),
      filter: (value, record) =>
        record.serviceCategoryName.toLowerCase().includes(value.toLowerCase()),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) =>
        record.serviceCategoryName.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Service Types",
      dataIndex: "childRecords",
      key: "serviceTypes",
      sorter: (a, b) => a.serviceTypeName.localeCompare(b.serviceTypeName),
      filter: (value, record) =>
        record.serviceTypeName.toLowerCase().includes(value.toLowerCase()),
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) =>
        record.serviceTypeName.toLowerCase().includes(value.toLowerCase()),
      render: (i, action) => (
        <>
          {action.typeName}
          {action.quantity <= action.reorderLevel && action.type === 1 && (
            <span className="text-danger mt-1 d-block">Low stock level</span>
          )}
        </>
      ),
    },
    {
      title: "Stock Qty",
      dataIndex: "status",
      key: "status",
      render: (i, action) => {
        if (action.type === 1) {
          return (
            <div className="d-flex align-item-center justify-content-start">
              <b className="d-inline-block mt-1 me-2">{action.quantity}</b>
              {isPermissionGranted(1003) && (
                <button
                  type="button"
                  className="btn btn-dark px-2 py-1 mb-0 float-end"
                  onClick={() => handleShowModal(action)}
                >
                  +
                </button>
              )}
            </div>
          );
        } else {
          return "";
        }
      },
    },
    {
      title: "Rate per item",
      dataIndex: "actions",
      key: "actions",
      render: (i, action) => {
        return (
          <>
            <div className="">
              {isPermissionGranted(1003) &&
              (user.role_Id !== 7 || action.type === 1) ? (
                <input
                  className="form-control"
                  type="text"
                  style={{ maxWidth: "100px" }}
                  value={rateData[action.childId] ?? action.rate} // Use rateData if available
                  onChange={(e) => handleRateChange(action, e.target.value)} // Capture input changes
                  onBlur={() => handleRateUpdate(action)} // Trigger API call onBlur
                />
              ) : (
                <div className="d-flex align-item-center justify-content-start">
                  <b className="d-inline-block mt-1 me-2">{action.rate}</b>
                </div>
              )}
            </div>
          </>
        );
      },
    },
  ];

  return (
    <>
      <div className="row align-items-center justify-content-between mb-2">
        <div className="col">
          <h5 className="fw-500 text-white">Inventory/Rate Chart</h5>
        </div>
        <div className="col-auto">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowBulkModal(true)}
          >
            <i className="fas fa-upload me-1"></i> Bulk Stock Update
          </button>
        </div>
      </div>
      <div className="card mb-4">
        <div className="card-header p-4">
          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by Product/ Service Name"
                value={sortFilter?.search || ""}
                onChange={(e) =>
                  setSortFilter({ ...sortFilter, search: e.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                onChange={(e) =>
                  setSortFilter({ ...sortFilter, outletID: e.target.value })
                }
                value={sortFilter?.outletID}
                disabled={
                  serviceList.isSuccess === true &&
                  !isDropdownPermission(user.role_Id)
                }
              >
                <option value="">All Category</option>
                {outletList?.data?.data.map((elem, i) => (
                  <option value={elem.outletId} key={i}>
                    {elem.outletName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-control"
                value={sortFilter?.type || ""}
                onChange={(e) =>
                  setSortFilter({ ...sortFilter, type: e.target.value })
                }
              >
                <option value="">Choose Service Type</option>
                {serviceTypeApi?.data?.data.map((elem, i) => (
                  <option value={elem.id} key={i}>
                    {elem.serviceTypeName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {serviceProductsApi.isLoading && <Loader height={500} />}
          {serviceProductsApi.isSuccess && (
            <Table columns={columns} dataSource={transformedData} />
          )}

          {/* Bulk Stock Update Modal */}
          {BulkStockUpdateModal()}

          {/* Bootstrap Modal for Adding Stock */}
          <Modal show={showModal} onHide={handleCloseModal}>
            <Modal.Header closeButton>
              <Modal.Title>
                Add Stock for {selectedItem?.productServiceName}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <input
                type="number"
                className="form-control"
                placeholder="Enter new stock quantity"
                value={newStockQuantity}
                onChange={(e) => setNewStockQuantity(e.target.value)}
              />
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn btn-sm bg-danger btn-danger mb-0"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleAddStock}
                className="btn btn-sm btn-primary mb-0"
              >
                Add Stock
              </button>
            </Modal.Footer>
          </Modal>

          {/* Confirmation Modal */}
          <Modal
            show={showConfirmationModal}
            onHide={() => setShowConfirmationModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Action</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to{" "}
              {confirmationAction === "addStock"
                ? "add stock"
                : "update the rate"}
              ?
            </Modal.Body>
            <Modal.Footer className="justify-content-center">
              <button
                type="button"
                onClick={() => setShowConfirmationModal(false)}
                className="btn btn-sm bg-danger btn-danger mb-0"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAction}
                className="btn btn-sm btn-primary mb-0"
              >
                Confirm
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default RateChart;
